import React, { useState } from 'react'
import {
  useContainerWidth,
  Button,
  useOverlay,
  BillboardButton,
  Separator,
  H2,
  FullPageOverlay,
  Theme,
} from '@fs/zion-ui'
import { PlaceWorld, PersonAdd, ThingCalendar } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { appPath } from '@fs/zion-config'
import { useFeatureFlag } from '@fs/zion-flags'
import Tile from './Tile'
import YearPicker from './YearPicker/YearPicker'
import { useDSCState } from '../../../state/DSCStore'
import Masonry from './Masonry'
import TileContent from './TileContent'
import AAMCountrySelector from './AAMCountrySelector'
import { useCorrections } from '../hooks/hooks'
import useFamilyTreeData from '../../../shared/hooks/useFamilyTreeData'

const colorList = [
  {
    // Light Green
    background: '#bed21e',
  },
  {
    // Green
    background: '#6db344',
  },
  {
    // Blue
    background: '#01b6d1',
  },
  {
    // Light Blue
    background: '#7de3f4',
  },
  {
    // Light Orange / Yellow
    background: '#ffb81c',
  },
  {
    // Orange
    background: '#f68d2e',
  },
  {
    // Pink
    background: '#fc4e6d',
  },
]

const urlParams = new URLSearchParams(window.location.search)
const collection = urlParams.get('collection')
const experience = urlParams.get('experience')

const collections = {
  names: ['NAME MEANING 4', 'NAME POPULARITY 28', 'MY HERITAGE 27'],
  year: [
    'POPULATION QUIZ 1',
    'COST COMPARISON 2',
    'MOVIE TICKET 3',
    'SPORTS TRIVIA 5',
    'SPORTS TRIVIA 6',
    'SPORTS TRIVIA 7',
    'SPORTS TRIVIA 8',
    'SPORTS TRIVIA 9',
    'SCIENCE AND TECHNOLOGY 10',
    'COST COMPARISON 11',
    'TENNIS 12',
    'TOP STORIES 13',
    'US HISTORY QUIZ 14',
    'POPULATION QUIZ 15',
    'SPORTS TRIVIA 17',
    'CHURCH HISTORY QUIZ 29',
    'TEMPLE DEDICATIONS 30',
    'CHURCH HISTORY QUIZ 31',
    'TOP GROSSING FILM 33',
    'POPULAR SONG 36',
    'SONG OF THE YEAR 37',
    'ALBUM OF THE YEAR 38',
  ],
}

const getCardColors = (index) => {
  const selectedColors = {
    background: '#DADADA',
    title: 'rgba(0,0,0,0.4)',
    text: '#000',
    icon: '#fff',
  }

  // cycle through the list of global default colors

  let i = (index % colorList.length) - 1
  if (i === -1) {
    i = colorList.length - 1
  }

  selectedColors.background = colorList[i].background || '#DADADA'
  selectedColors.title = colorList[i].title || 'rgba(0,0,0,0.4)'
  selectedColors.text = colorList[i].text || '#000'
  selectedColors.icon = colorList[i].icon || '#fff'

  return selectedColors
}

export default function AAMContent({ tileData, year, user }) {
  const [t] = useTranslation()
  if (user?.gender) {
    tileData.forEach((item) => {
      item.gender = user.gender
    })
  }
  const { data: treeData, isFetched: treeDataFetched } = useFamilyTreeData(3)

  // Create wrapper object to match old state structure for backwards compatibility
  const treeDataState = {
    complete: treeDataFetched,
    res: treeData,
  }
  const [openTile, setOpenTile] = useState({})
  const atWidth = useContainerWidth()
  const countryOverlay = useOverlay()
  const overlay = useOverlay()
  const tileDataOverlay = useOverlay()
  const isCorrections = useCorrections()
  const exitCorrections = () => {
    sessionStorage.removeItem('FS.ds.nonstandardUser')
    window.location.href = `${appPath}/about/correctionslogin`
  }
  const { isOpen } = overlay
  const { isOpen: isCountryOpen } = countryOverlay
  const { isOpen: isTileOpen } = tileDataOverlay

  const aamCustomNameFlag = useFeatureFlag('discovery_aam_custom_name')

  const dscState = useDSCState()

  const rootContainerStyle = css`
    max-width: 100%;
    max-height: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
    background-color: #fff;
    margin: 10px;
  `
  const masonryWrapperCSS = css`
    margin: ${atWidth({ default: '0 10px 0 10px', lg: '0 30px 0 30px' })};
  `
  const topStyle = css`
    display: flex;
    justify-content: space-between;
    flex-direction: ${atWidth({ default: 'column', lg: 'row' })};
    flex-wrap: wrap;
    margin-inline-end: 30px;
    margin-bottom: 30px;
    margin-inline-start: 30px;
    ${atWidth({ default: 'align-items: center', md: null })};
    button {
      align-self: ${atWidth({ default: 'flex-start', md: 'center' })};
    }
    header {
      margin: ${atWidth({ default: '10px 0', lg: '0' })};
    }
  `

  let yearHeader = t('aam.heres-what-was-happening', `Here's what was happening in`)
  yearHeader = `${yearHeader} ${year}`

  const columnCount = atWidth({ default: 1, sm: 1, lg: 2, xl: 3, xxl: 4 })
  const country = t(`global.countries.country${tileData?.[0]?.region.toUpperCase()}`)
  const language = t(`global.countries.language.${tileData?.[0]?.language}`)

  const tileContentWrapperCss = css`
    height: 100%;
    & > div {
      height: 100%;
    }
  `

  return (
    <div css={rootContainerStyle}>
      <div css={topStyle}>
        {user?.signedIn && (
          <Button
            Icon={PlaceWorld}
            emphasis="low"
            data-testid="Country"
            linkName="AAMContent:OPEN_LANUAGE_DIALOG:CLICK"
            onClick={countryOverlay.handleClick}
          >
            {tileData[0] ? `${country} - ${language}` : `${t('aam.content.country.loading', 'Loading...')}`}
          </Button>
        )}
        <H2 size={atWidth({ default: 'H4', lg: 'H3' })}>{yearHeader}</H2>
        <Separator size={atWidth({ default: 'lg', lg: 'xl' })} />
        {aamCustomNameFlag.isOn ? (
          <BillboardButton
            Icon={PersonAdd}
            data-testid="Person"
            linkName="AAMContent:VIEW_DIFFERENT_PERSON:CLICK"
            onClick={overlay.open}
          >
            &nbsp;{t('aam.view-different-person-year', 'View a different person or year')}
          </BillboardButton>
        ) : (
          <BillboardButton
            Icon={ThingCalendar}
            data-testid="Year"
            linkName="AAMContent:VIEW_DIFFERENT_YEAR:CLICK"
            onClick={overlay.open}
          >
            &nbsp;{t('aam.view-different-year', 'View a different year')}
          </BillboardButton>
        )}
        {isCorrections && (
          <BillboardButton data-testid="aam-content-billboard-button" onClick={exitCorrections}>
            &nbsp;{t('aam.exit', 'Exit')}
          </BillboardButton>
        )}
      </div>
      {isCountryOpen && <AAMCountrySelector overlay={countryOverlay} />}
      {isOpen && (
        <YearPicker
          overlay={overlay}
          tileData={tileData}
          showAncestorList={
            (dscState?.nonstandardUser?.initialized && dscState.nonstandardUser?.authenticated) || user?.signedIn
          }
        />
      )}
      <div css={masonryWrapperCSS}>
        <Masonry gap="20px" columnCount={columnCount}>
          {tileData &&
            tileData
              .filter((tile) =>
                collection
                  ? collections[collection] && collections[collection].indexOf(`${tile?.title} ${tile?.id}`) > -1
                  : true
              )
              .map((tile, indx) => (
                <Tile
                  key={`${tile?.title}_${tile?.id}`}
                  index={indx}
                  autoload={`${tile?.title} ${tile?.id}` === experience}
                  color={getCardColors(indx)?.background}
                  tileData={tile}
                  setOpenTile={setOpenTile}
                  overlay={tileDataOverlay}
                  openTile={openTile}
                />
              ))}
        </Masonry>
        {isTileOpen && openTile && (
          <FullPageOverlay
            {...tileDataOverlay}
            onClose={() => {
              tileDataOverlay.close()
              setOpenTile(null)
            }}
            title={t('common-ui:back.action', 'Back')}
          >
            <div css={tileContentWrapperCss}>
              <Theme theme="night">
                <TileContent
                  tileData={openTile}
                  treeDataState={treeDataState}
                  highlightColor={openTile?.highlightColor}
                />
              </Theme>
            </div>
          </FullPageOverlay>
        )}
      </div>
    </div>
  )
}
